/**
 * Tenant Admin — Order Returns queue.
 *
 * Customer requests a return on a DELIVERED order (see
 * `CustomerReferral`-adjacent `/orders/{id}` "Request Return" flow on
 * `OrderTrackPage.jsx`); operators here Approve (refunds wallet + coins)
 * or Reject (reason only, no refund) each PENDING request. Same
 * shape/testid convention as `AdminWalletWithdrawals.jsx` — Approve/Reject
 * share one modal, filter pills for status.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, XCircle, IndianRupee, Search, Undo2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr2 } from "../../lib/api";

const STATUSES = [
    { value: "PENDING", label: "Pending", color: "bg-amber-100 text-amber-700" },
    { value: "APPROVED", label: "Approved", color: "bg-emerald-100 text-emerald-700" },
    { value: "REJECTED", label: "Rejected", color: "bg-red-100 text-red-700" },
];

export default function AdminReturns() {
    const [filter, setFilter] = useState("PENDING");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [action, setAction] = useState(null);   // { rr, mode: 'approve'|'reject' }
    const [reason, setReason] = useState("");
    const [refundAmount, setRefundAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [windowDays, setWindowDays] = useState("");
    const [savingWindow, setSavingWindow] = useState(false);

    useEffect(() => {
        api.get("/admin/tenant-settings").then(({ data }) => {
            setWindowDays(String(data?.return_window_days ?? 7));
        }).catch(() => {});
    }, []);

    const saveWindow = async () => {
        const days = Number(windowDays);
        if (!Number.isInteger(days) || days < 1 || days > 90) {
            toast.error("Enter a whole number of days between 1 and 90");
            return;
        }
        setSavingWindow(true);
        try {
            await api.patch("/admin/tenant-settings", { return_window_days: days });
            toast.success(`Return window set to ${days} day${days !== 1 ? "s" : ""}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save the return window");
        } finally { setSavingWindow(false); }
    };

    // Manual refund — pre-fill with the full eligible amount, but the
    // admin can lower it for a partial refund. Automatic/system refunds
    // (not this UI) always send no amount and get the full eligible
    // amount server-side.
    const openApprove = (rr) => {
        setAction({ rr, mode: "approve" });
        setRefundAmount(String(rr.max_refund_amount ?? ""));
    };

    const reload = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/returns?status=${filter}`);
            setRows(data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
        finally { setBusy(false); }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await reload(); })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => (r.reason || "").toLowerCase().includes(q) ||
            (r.order_id || "").toLowerCase().includes(q));
    }, [rows, search]);

    const submitAction = async () => {
        if (!action) return;
        setSubmitting(true);
        try {
            if (action.mode === "approve") {
                const amt = Number(refundAmount);
                if (!amt || amt <= 0) { toast.error("Enter a refund amount greater than 0"); setSubmitting(false); return; }
                const max = action.rr.max_refund_amount;
                if (max != null && amt > max) { toast.error(`Amount can't exceed the eligible ${inr2(max)}`); setSubmitting(false); return; }
                await api.post(`/admin/returns/${action.rr.id}/approve`, { refund_amount: amt });
                toast.success(`Return approved — ${inr2(amt)} credited to wallet`);
            } else {
                if (!reason.trim() || reason.trim().length < 3) { toast.error("Enter a clear rejection reason"); setSubmitting(false); return; }
                await api.post(`/admin/returns/${action.rr.id}/reject`, { reason: reason.trim() });
                toast.success("Return rejected");
            }
            setAction(null); setReason(""); setRefundAmount("");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSubmitting(false); }
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Undo2 size={22} /> Order Returns</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Approve (refund to wallet) or reject customer return requests on delivered orders.</p>
                </div>
                {filter === "PENDING" && rows.length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-xl px-4 py-2 text-xs" data-testid="return-pending-kpi">
                        <span className="font-bold text-amber-700 dark:text-amber-300">{rows.length}</span> pending
                    </div>
                )}
            </div>

            <div className="bg-card rounded-2xl border border-border p-4 mb-3 flex items-center gap-3 flex-wrap" data-testid="return-window-settings">
                <Settings2 size={16} className="text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-[220px]">
                    <p className="text-sm font-bold">Return window</p>
                    <p className="text-xs text-muted-foreground">
                        Customers can request a return within this many days of delivery.
                    </p>
                </div>
                <input
                    type="number" min="1" max="90" step="1"
                    value={windowDays}
                    onChange={(e) => setWindowDays(e.target.value)}
                    className="w-20 bg-muted rounded-lg px-3 py-2 text-sm text-center outline-none"
                    data-testid="return-window-days-input"
                />
                <span className="text-xs text-muted-foreground">days</span>
                <button
                    onClick={saveWindow}
                    disabled={savingWindow}
                    className="rounded-lg bg-primary text-primary-foreground px-3 py-2 text-xs font-bold disabled:opacity-60"
                    data-testid="return-window-days-save"
                >
                    {savingWindow ? <Loader2 size={13} className="animate-spin" /> : "Save"}
                </button>
            </div>

            <div className="bg-card rounded-2xl border border-border p-2 mb-3 flex items-center gap-2">
                <div className="flex gap-1 p-0.5 bg-muted rounded-lg" data-testid="return-filter-tabs">
                    {STATUSES.map((s) => (
                        <button
                            key={s.value}
                            onClick={() => setFilter(s.value)}
                            data-testid={`return-filter-${s.value}`}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md ${filter === s.value ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}
                        >{s.label}</button>
                    ))}
                </div>
                <div className="flex-1 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search order id / reason"
                        className="w-full bg-muted rounded-lg pl-8 pr-3 py-2 text-xs outline-none"
                        data-testid="return-search"
                    />
                </div>
            </div>

            <div className="bg-card rounded-2xl border border-border overflow-x-auto" data-testid="return-table">
                {busy ? (
                    <div className="py-16 text-center"><Loader2 className="animate-spin inline" /></div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground text-sm">No return requests.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-left">
                            <tr>
                                <Th>Order</Th><Th>Reason</Th><Th>Requested</Th>
                                <Th>Status</Th><Th>Refund / Reject reason</Th><Th className="text-right">Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((rr) => (
                                <tr key={rr.id} className="border-t border-border" data-testid={`return-row-${rr.id}`}>
                                    <Td><span className="font-mono text-xs">{rr.order_id}</span></Td>
                                    <Td><p className="text-xs max-w-xs line-clamp-2">{rr.reason}</p></Td>
                                    <Td><span className="text-xs text-muted-foreground">{new Date(rr.requested_at || rr.created_at).toLocaleString()}</span></Td>
                                    <Td><Badge status={rr.status} /></Td>
                                    <Td>
                                        {rr.status === "APPROVED" && (
                                            <span className="font-bold inline-flex items-center text-emerald-700"><IndianRupee size={11} />{Math.round(rr.refund_amount || 0).toLocaleString("en-IN")}</span>
                                        )}
                                        {rr.reject_reason && <span className="text-[11px] text-red-600">{rr.reject_reason}</span>}
                                    </Td>
                                    <Td className="text-right">
                                        {rr.status === "PENDING" ? (
                                            <div className="inline-flex gap-1">
                                                <button onClick={() => openApprove(rr)} className="rounded-lg bg-emerald-600 text-white px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1" data-testid={`return-approve-${rr.id}`}><CheckCircle2 size={12} /> Approve</button>
                                                <button onClick={() => setAction({ rr, mode: "reject" })} className="rounded-lg bg-red-600 text-white px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1" data-testid={`return-reject-${rr.id}`}><XCircle size={12} /> Reject</button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">{new Date(rr.processed_at || rr.updated_at).toLocaleDateString()}</span>
                                        )}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {action && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !submitting && setAction(null)}>
                    <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="return-action-modal">
                        <h2 className="text-lg font-bold mb-1">{action.mode === "approve" ? "Approve Return" : "Reject Return"}</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            Order <span className="font-mono">{action.rr.order_id}</span> — {action.rr.reason}
                        </p>
                        {action.mode === "approve" ? (
                            <>
                                <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Refund amount (₹)</label>
                                <input
                                    type="number" min="0" step="0.01"
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(e.target.value)}
                                    className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                                    data-testid="return-action-amount"
                                />
                                <p className="text-[10px] text-muted-foreground mt-2">
                                    Pre-filled with the full eligible amount ({inr2(action.rr.max_refund_amount || 0)}, incl. any wallet-usage leg) — lower it for a partial refund. Any coins redeemed on this order will also be refunded.
                                </p>
                            </>
                        ) : (
                            <>
                                <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Reason (visible to customer)</label>
                                <textarea
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Item condition doesn't meet return policy"
                                    className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                                    data-testid="return-action-reason"
                                />
                                <p className="text-[10px] text-muted-foreground mt-2">No refund is issued on rejection.</p>
                            </>
                        )}
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setAction(null)} className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold" data-testid="return-action-cancel">Cancel</button>
                            <button onClick={submitAction} disabled={submitting} className={`flex-1 rounded-xl ${action.mode === "approve" ? "bg-emerald-600" : "bg-red-600"} text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60`} data-testid="return-action-confirm">{submitting && <Loader2 size={13} className="animate-spin" />}Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

function Badge({ status }) {
    const cfg = STATUSES.find((s) => s.value === status) || { color: "bg-muted", label: status };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>;
}
